import React, { Suspense, lazy, useEffect, useState } from 'react'
import { Button, Table } from 'react-bootstrap'
import ReactPaginate from 'react-paginate'
import { useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import Swal from 'sweetalert2'
import { useImmer } from 'use-immer'
import DeleteIcon from '../../../components/AllSvgIcon/DeleteIcon'
import EditIcon from '../../../components/AllSvgIcon/EditIcon'
import Loader from '../../../components/Loader'
import SearchBox from '../../../components/Searchbox'
import CustomToast from '../../../components/Toast/CustomToast'
import { customStyles } from '../../../components/customStyles'
import {
  calculatePageRange,
  encodeId,
  encodedSearchText,
  showToast
} from '../../../lib/AllGlobalFunction'
import {
  allCrudNames,
  allPages,
  checkPageAccess
} from '../../../lib/AllPageNames'
import { pageRangeDisplayed } from '../../../lib/AllStaticVariables'
import axiosProvider from '../../../lib/AxiosProvider'
import { _SwalDelete, _exception } from '../../../lib/exceptionMessage'
import useDebounce from '../../../lib/useDebounce'
import NotFound from '../../NotFound/NotFound'

const StaticPageForm = lazy(() => import('./StaticPageForm'))

const StaticPageList = () => {
  const [modalShow, setModalShow] = useState(false)
  const [data, setData] = useState()
  const [activeToggle, setActiveToggle] = useState('staticPage')
  const [searchText, setSearchText] = useState()
  const [loading, setLoading] = useState(false)
  const initVal = {
    name: '',
    link: '',
    pageContent: ''
  }
  const [initialValues, setInitialValues] = useState(initVal)
  const [toast, setToast] = useState({
    show: false,
    text: null,
    variation: null
  })
  const [filterDetails, setFilterDetails] = useImmer({
    pageIndex: 1,
    pageSize: 50,
    searchText: ''
  })
  const { userInfo, pageAccess } = useSelector((state) => state?.user)
  const location = useLocation()
  const debounceSearchText = useDebounce(searchText, 500)

  const handleTabClick = (e, tabName) => {
    e.preventDefault()
    setActiveToggle(tabName)
  }

  const fetchData = async () => {
    try {
      setLoading(true)

      const response = await axiosProvider({
        method: 'GET',
        endpoint: 'ManageStaticPages/search',
        queryString: `?searchText=${encodedSearchText(
          filterDetails?.searchText
        )}&pageIndex=${filterDetails?.pageIndex}&pageSize=${
          filterDetails?.pageSize
        }`
      })
      setLoading(false)
      if (response?.status === 200) {
        setData(response)
      }
    } catch {
      setLoading(false)
    }
  }

  const handleDelete = async (id) => {
    try {
      setLoading(true)
      const response = await axiosProvider({
        method: 'DELETE',
        endpoint: 'ManageStaticPages',
        queryString: `?Id=${id}`,
        userId: userInfo?.userId,
        location: location.pathname
      })

      setLoading(false)
      if (response?.data?.code === 200) {
        fetchData()
      }
      showToast(toast, setToast, response)
    } catch {
      setLoading(false)

      showToast(toast, setToast, {
        data: {
          message: _exception?.message,
          code: 204
        }
      })
    }
  }

  const handlePageClick = (event) => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    setFilterDetails((draft) => {
      draft.pageIndex = event.selected + 1
    })
  }

  useEffect(() => {
    fetchData()
  }, [filterDetails])

  useEffect(() => {
    if (debounceSearchText) {
      setFilterDetails((draft) => {
        draft.searchText = debounceSearchText
        draft.pageIndex = 1
      })
    } else {
      setFilterDetails((draft) => {
        draft.searchText = ''
        draft.pageIndex = 1
      })
    }
  }, [debounceSearchText])

  return checkPageAccess(
    pageAccess,
    allPages?.manageStaticPage,
    allCrudNames?.read
  ) ? (
    <div className='card overflow-hidden'>
      <div className='card-body p-0'>
        <div className='nav-tabs-horizontal nav nav-tabs'>
          <Link
            onClick={(e) => handleTabClick(e, 'staticPage')}
            data-toggle='tab'
            className={`nav-link fw-semibold ${
              activeToggle === 'staticPage' ? 'active show' : ''
            }`}
          >
            <span className='nav-span'>
              Static Page
              <hr />
            </span>
          </Link>
        </div>

        <div className='tab-content p-3'>
          <div
            id='staticPage'
            className={`tab-pane fade ${
              activeToggle === 'staticPage' ? 'active show' : ''
            }`}
          >
            {activeToggle === 'staticPage' && (
              <div>
                <div className='d-flex align-items-center mb-3 gap-3 flex-row-reverse'>
                  {checkPageAccess(
                    pageAccess,
                    allPages?.manageStaticPage,
                    allCrudNames?.write
                  ) && (
                    <Button
                      variant='warning'
                      className='d-flex align-items-center gap-2 fw-semibold btn btn-warning ms-auto'
                      onClick={() => {
                        setInitialValues(initVal)
                        setModalShow(true)
                      }}
                    >
                      <i className='m-icon m-icon--plusblack'></i>
                      Create
                    </Button>
                  )}

                  <div className='page-range'>
                    {calculatePageRange({
                      ...filterDetails,
                      recordCount: data?.data?.pagination?.recordCount ?? 0
                    })}
                  </div>

                  <div className='d-flex align-items-center me-2'>
                    <label className='me-1'>Show</label>
                    <select
                      styles={customStyles}
                      name='dataget'
                      className='form-select me-1'
                      value={filterDetails?.pageSize}
                      onChange={(e) => {
                        setFilterDetails((draft) => {
                          draft.pageSize = e?.target?.value
                        })
                      }}
                    >
                      <option value='10'>10</option>
                      <option value='25'>25</option>
                      <option value='50'>50</option>
                      <option value='100'>100</option>
                      <option value='200'>200</option>
                      <option value='500'>500</option>
                    </select>
                  </div>

                  <SearchBox
                    placeholderText={'Search'}
                    value={searchText}
                    searchclassnamewrapper={'searchbox-wrapper'}
                    onChange={(e) => {
                      setSearchText(e?.target?.value)
                    }}
                  />
                </div>

                <Table responsive className='align-middle table-list'>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Link</th>
                      {checkPageAccess(pageAccess, allPages?.manageStaticPage, [
                        allCrudNames?.update,
                        allCrudNames?.delete
                      ]) && <th className='text-center'>Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {data?.data?.data?.length > 0 ? (
                      data?.data?.data.map((data) => (
                        <tr key={data.id}>
                          <td>{data?.name}</td>
                          <td>{`static?id=${encodeId(data?.id)}`}</td>
                          {checkPageAccess(
                            pageAccess,
                            allPages?.manageStaticPage,
                            [allCrudNames?.update, allCrudNames?.delete]
                          ) && (
                            <td className='text-center'>
                              <div className='d-flex gap-2 justify-content-center'>
                                {checkPageAccess(
                                  pageAccess,
                                  allPages?.manageStaticPage,
                                  allCrudNames?.update
                                ) && (
                                  <span
                                    onClick={() => {
                                      setModalShow(true)
                                      setInitialValues(data)
                                    }}
                                  >
                                    <EditIcon bg={'bg'} />
                                  </span>
                                )}
                                {checkPageAccess(
                                  pageAccess,
                                  allPages?.manageStaticPage,
                                  allCrudNames?.delete
                                ) && (
                                  <span
                                    onClick={() => {
                                      Swal.fire({
                                        title: _SwalDelete.title,
                                        text: _SwalDelete.text,
                                        icon: _SwalDelete.icon,
                                        showCancelButton:
                                          _SwalDelete.showCancelButton,
                                        confirmButtonColor:
                                          _SwalDelete.confirmButtonColor,
                                        cancelButtonColor:
                                          _SwalDelete.cancelButtonColor,
                                        confirmButtonText:
                                          _SwalDelete.confirmButtonText,
                                        cancelButtonText:
                                          _SwalDelete.cancelButtonText
                                      }).then((result) => {
                                        if (result.isConfirmed) {
                                          handleDelete(data?.id)
                                        } else if (result.isDenied) {
                                        }
                                      })
                                    }}
                                  >
                                    <DeleteIcon bg={'bg'} />
                                  </span>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={3} className='text-center'>
                          {data?.data?.message}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>

                {data?.data?.pagination?.pageCount > 0 && (
                  <ReactPaginate
                    className='list-inline m-cst--pagination d-flex justify-content-end gap-1'
                    breakLabel='...'
                    nextLabel=''
                    onPageChange={handlePageClick}
                    pageRangeDisplayed={pageRangeDisplayed}
                    pageCount={data?.data?.pagination?.pageCount}
                    previousLabel=''
                    renderOnZeroPageCount={null}
                    forcePage={filterDetails?.pageIndex - 1}
                  />
                )}

                {loading && !modalShow && <Loader />}

                {toast?.show && !modalShow && (
                  <CustomToast
                    text={toast?.text}
                    variation={toast?.variation}
                  />
                )}

                <Suspense fallback={<Loader />}>
                  {modalShow && (
                    <StaticPageForm
                      initialValues={initialValues}
                      modalShow={modalShow}
                      setModalShow={setModalShow}
                      loading={loading}
                      setLoading={setLoading}
                      fetchData={fetchData}
                      toast={toast}
                      setToast={setToast}
                    />
                  )}
                </Suspense>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  ) : (
    <NotFound />
  )
}

export default StaticPageList
